import type { ErrorCode, FieldProblem, ProblemDocument, ProblemParams } from './types.ts'

/** Every failure a caller sees, normalised: `code` and `params` feed the message layer, `title` and
 * `detail` are English, for logs, and never reach the screen —
 * packages/api-client/README.md § «`title` e `detail` non si mostrano a schermo». */

/** A failure that did not come back as an HTTP problem document. */
export type TransportFailure = 'network' | 'aborted' | 'malformed_response'

/**
 * Base class for everything thrown by the client, so a caller can `catch` one type and
 * branch on `code`.
 *
 * `message` is English: it exists for the log line, and is built from `detail` when the
 * backend sent one.
 */
export class ApiError extends Error {
  /** HTTP status, or 0 when the request never got an answer. */
  readonly status: number

  constructor(message: string, status: number, options?: ErrorOptions) {
    super(message, options)
    this.name = 'ApiError'
    this.status = status
  }
}

/**
 * An RFC 9457 problem document from the API.
 *
 * `code` is the i18n key; `params` are its interpolation values. Both may be absent —
 * an error from a proxy or a gateway is not required to speak our dialect — so the
 * message layer needs a fallback for `code === null`.
 */
export class ProblemError extends ApiError {
  readonly code: ErrorCode | null
  readonly params: ProblemParams | null
  /** RFC 9457 problem type URI, e.g. `/problems/not-found`. */
  readonly type: string | null
  /** English, for developers. **Never display.** */
  readonly title: string | null
  /** English, for developers and logs. **Never display.** */
  readonly detail: string | null
  readonly instance: string | null
  /** Matches the backend log line and the `X-Request-ID` header. Quote it in a bug report. */
  readonly requestId: string | null

  constructor(status: number, document: ProblemDocument, requestId: string | null) {
    // The English detail is the most useful thing in a log; the code identifies the case.
    super(document.detail ?? document.title ?? `Request failed with status ${status}`, status)
    this.name = 'ProblemError'
    this.code = document.code ?? null
    this.params = document.params ?? null
    this.type = document.type ?? null
    this.title = document.title ?? null
    this.detail = document.detail ?? null
    this.instance = document.instance ?? null
    this.requestId = requestId
  }
}

/**
 * A 422 carrying field errors.
 *
 * `fields` can be empty: an `UnprocessableError` raised by domain code is also a 422 and
 * carries none, which is why the backend declares `errors` optional. Callers must not
 * assume a non-empty list.
 */
export class ValidationError extends ProblemError {
  readonly fields: readonly FieldProblem[]

  constructor(status: number, document: ProblemDocument, requestId: string | null) {
    super(status, document, requestId)
    this.name = 'ValidationError'
    this.fields = document.errors ?? []
  }

  /** Field errors keyed by field path, for binding to form inputs. */
  byField(): ReadonlyMap<string, readonly FieldProblem[]> {
    const grouped = new Map<string, FieldProblem[]>()
    for (const field of this.fields) {
      const existing = grouped.get(field.field)
      if (existing) existing.push(field)
      else grouped.set(field.field, [field])
    }
    return grouped
  }
}

/**
 * The request never produced a problem document: DNS failure, offline, CORS, abort, or a
 * body that was not the JSON we expected.
 *
 * Normalised into the same family on purpose — a caller should never have to catch a raw
 * `TypeError: Failed to fetch` from this package.
 */
export class TransportError extends ApiError {
  readonly failure: TransportFailure

  constructor(failure: TransportFailure, message: string, options?: ErrorOptions) {
    super(message, 0, options)
    this.name = 'TransportError'
    this.failure = failure
  }
}

/** Narrowing helpers, so callers do not import the classes just to use `instanceof`. */
export function isProblemError(error: unknown): error is ProblemError {
  return error instanceof ProblemError
}

export function isValidationError(error: unknown): error is ValidationError {
  return error instanceof ValidationError
}

export function isTransportError(error: unknown): error is TransportError {
  return error instanceof TransportError
}
